/**
 * Folio's DEMO catalog — the SAFHA bookshop, and what a grid falls back to when nothing real resolves.
 *
 * It lives in ui-react, not in the storefront app, because the BUILDER needs it too: a seller opening
 * Folio on an empty store would otherwise get the house fallback (a clothing shop, "Cotton Crew Tee")
 * inside a bookshop theme (spec 0032 §3 always listed `catalog` as one of a theme's four pieces).
 *
 * TYPE-ONLY IMPORTS on purpose: `sections/_shared/product.ts` reads this module through
 * `themes/demoCatalogs.ts`, so anything but a type import here would close an import cycle.
 *
 * Prices carry the STORE's currency rather than one of their own — the same demo has to read correctly
 * in a MAD shop and in a EUR one (0049).
 *
 * THE TITLES ARE NOT INVENTED HERE. Every name and author below is a TRANSCRIPTION of what is actually
 * printed on the photographed cover, read off the image after generating it. The order is deliberate and
 * it is the opposite of the other themes: a bottle can be photographed without a label, a book cannot be
 * photographed without its title, so the picture had to come first and the data second. Doing it the
 * usual way round is how you end up with « LE SEL DES JOURS » on the photograph and « Le Sel du Jour »
 * in the product sheet.
 *
 * The authors are invented. The first brand board came back signing two of the six novels
 * « Jean-Claude Carrière » and « Antoine de Saint-Exupéry » — real writers — and was thrown away and
 * regenerated: a template that ships to sellers must not attribute a made-up novel to a real person.
 */
const IMG = '/demo/books';
/**
 * A book's real variant is its FORMAT, not a size or a flavour. Naming the option « Format » with
 * Relié/Broché is what makes the buy-box read like a bookshop instead of a clothing store.
 */
const FORMATS = ['Relié', 'Broché'];
function book(currency, p) {
    return {
        id: p.id,
        name: p.name,
        slug: p.slug,
        description: p.desc,
        price: { amount: p.price, currency },
        compareAtPrice: p.compareAt ? { amount: p.compareAt, currency } : null,
        // `StorefrontImage` ne porte PAS de width/height (contrairement à `ImageValue` des documents) :
        // la galerie dimensionne par CSS. Les ajouter compile en erreur (piège n°4 du mode d'emploi).
        images: [
            { url: `${IMG}/${p.image}`, alt: `${p.name}, de ${p.author} — couverture`, isCover: true },
        ],
        inventory: { tracked: false, quantity: null },
        seo: { metaTitle: null, metaDescription: null },
        status: 'active',
        tags: p.badge ? [p.badge] : [],
        // Le « vendeur » d'un livre est son AUTEUR, pas la librairie : c'est la seule niche du registre où
        // `showVendor` a un sens sur la carte produit, et c'est ce qui fait qu'une grille de livres se lit
        // comme une table de librairie et pas comme un rayon de supermarché.
        vendor: p.author,
        rating: p.rating,
        options: [{ name: 'Format', values: (p.formats ?? FORMATS).map((label) => ({ label })) }],
    };
}
/** La table SAFHA, dans l'ordre où la libraire la présente. */
export function folioDemoProducts(currency) {
    return [
        book(currency, {
            id: 'p-safha-sel',
            name: 'Le Sel des Jours',
            slug: 'le-sel-des-jours',
            author: 'Nora Belkhadir',
            price: 14900,
            compareAt: 17900,
            badge: 'custom-badge-1',
            image: 'cover-sel.webp',
            rating: { value: 4.9, count: 214 },
            desc: 'Trois générations d’une famille de Salé, racontées par les repas qu’elles ont partagés. Le premier roman de l’année qu’on a lu deux fois.',
        }),
        book(currency, {
            id: 'p-safha-medinas',
            name: 'Médinas',
            slug: 'medinas',
            author: 'Karim Essaadi',
            price: 49000,
            image: 'cover-medinas.webp',
            rating: { value: 4.8, count: 96 },
            desc: 'Douze ans de photographies dans les médinas de Fès, Tétouan et Essaouira. Grand format, papier mat, 240 planches.',
            formats: ['Relié'],
        }),
        book(currency, {
            id: 'p-safha-agadir',
            name: 'La Maison d’Agadir',
            slug: 'la-maison-d-agadir',
            author: 'Leïla Khatib',
            price: 16500,
            image: 'cover-agadir.webp',
            rating: { value: 4.7, count: 158 },
            desc: 'Une femme revient dans la maison que le tremblement de terre a laissée debout. Court, tendu, difficile à reposer.',
        }),
        book(currency, {
            id: 'p-safha-lire',
            name: 'L’Art de lire lentement',
            slug: 'l-art-de-lire-lentement',
            author: 'Inès Marchand',
            price: 13500,
            badge: 'custom-badge-2',
            image: 'cover-lire.webp',
            rating: { value: 4.6, count: 187 },
            desc: 'Un éloge de la relecture et des marges annotées, par une traductrice qui met six mois sur cent pages.',
        }),
        book(currency, {
            id: 'p-safha-encre',
            name: 'Poussière d’encre',
            slug: 'poussiere-d-encre',
            author: 'Amina Bensouda',
            price: 9500,
            image: 'cover-encre.webp',
            rating: { value: 4.8, count: 74 },
            desc: 'Quarante poèmes courts sur l’exil et le retour. À lire un le soir, pas quarante d’affilée.',
            formats: ['Broché'],
        }),
        book(currency, {
            id: 'p-safha-cartographe',
            name: 'Le Petit Cartographe',
            slug: 'le-petit-cartographe',
            author: 'Sofia Arraji',
            price: 8900,
            image: 'cover-cartographe.webp',
            rating: { value: 4.9, count: 231 },
            desc: 'Un enfant dessine la carte d’un pays qui n’existe pas, et finit par y arriver. Dès 7 ans, à lire à voix haute.',
        }),
        book(currency, {
            id: 'p-safha-fes',
            name: 'Les Nuits de Fès',
            slug: 'les-nuits-de-fes',
            author: 'Yassine Rochdi',
            price: 15900,
            image: 'cover-nuits-fes.webp',
            rating: { value: 4.7, count: 142 },
            desc: 'Un veilleur de nuit traverse la médina pendant douze nuits de Ramadan. Un roman qui se lit à l’heure où il se passe.',
        }),
        book(currency, {
            id: 'p-safha-atlas',
            name: 'Atlas des mots perdus',
            slug: 'atlas-des-mots-perdus',
            author: 'Clara Vidal',
            price: 18500,
            image: 'cover-atlas-mots.webp',
            rating: { value: 4.6, count: 88 },
            desc: 'Cent mots que les langues du pourtour méditerranéen ont cessé d’employer, et ce que leur disparition a emporté.',
        }),
        book(currency, {
            id: 'p-safha-tanger',
            name: 'Le Jardin de Tanger',
            slug: 'le-jardin-de-tanger',
            author: 'Mehdi Ouazzani',
            price: 14500,
            image: 'cover-jardin-tanger.webp',
            rating: { value: 4.5, count: 119 },
            desc: 'Deux frères héritent d’un jardin en terrasse et d’un désaccord vieux de trente ans. Drôle, puis moins.',
        }),
        book(currency, {
            id: 'p-safha-safi',
            name: 'Carnet de Safi',
            slug: 'carnet-de-safi',
            author: 'Rim Daoudi',
            price: 12900,
            image: 'cover-carnet-safi.webp',
            rating: { value: 4.7, count: 63 },
            desc: 'Six mois chez les potiers de Safi, carnet de croquis à la main. Un récit de voyage qui ne quitte jamais une seule ville.',
        }),
        book(currency, {
            id: 'p-safha-oliviers',
            name: 'La Voix des oliviers',
            slug: 'la-voix-des-oliviers',
            author: 'Samir Lahlou',
            price: 9900,
            image: 'cover-voix-oliviers.webp',
            rating: { value: 4.6, count: 57 },
            desc: 'Des poèmes écrits dans le Moyen Atlas, entre deux récoltes. Le recueil qu’on offre à quelqu’un qui ne lit pas de poésie.',
            formats: ['Broché'],
        }),
        book(currency, {
            id: 'p-safha-figuier',
            name: 'L’Enfant et le figuier',
            slug: 'l-enfant-et-le-figuier',
            author: 'Houda Naciri',
            price: 7900,
            compareAt: 9500,
            image: 'cover-enfant-figuier.webp',
            rating: { value: 4.8, count: 176 },
            desc: 'Une année entière au pied d’un figuier, saison par saison. Album illustré, dès 4 ans.',
            formats: ['Relié'],
        }),
    ];
}
/**
 * SIX rayons, et six est un choix de mise en page autant que de fond : `collection-list` plafonne à
 * quatre colonnes, donc six tuiles se rangent en DEUX rangées de trois — pleines toutes les deux. Cinq
 * ou sept laisseraient la colonne fantôme du piège n°3.
 *
 * La vignette d'un rayon vient TOUJOURS de la collection : le bloc de `collection-list` n'a qu'un champ
 * `ref`, aucune image ne peut être écrite dans le document (piège n°2). C'est donc ici, et nulle part
 * ailleurs, que vivent les six photos de rayon.
 */
export function folioDemoCollections() {
    const make = (id, name, slug, cover, alt, productCount, description) => ({
        id,
        name,
        slug,
        description,
        coverImage: { url: `${IMG}/${cover}`, alt },
        productCount,
    });
    return [
        make('c-romans', 'Romans', 'romans', 'rayon-romans.webp', 'Rayon de romans reliés sur une étagère vert bouteille', 5, 'Les histoires longues, celles qu’on emporte en voyage et qu’on finit avant d’être rentré.'),
        make('c-essais', 'Essais & Idées', 'essais-idees', 'rayon-essais.webp', 'Essais empilés sur un bureau en chêne, à côté d’une paire de lunettes', 2, 'Pour comprendre quelque chose qu’on croyait déjà comprendre.'),
        make('c-poesie', 'Poésie', 'poesie', 'rayon-poesie.webp', 'Recueil de poésie ouvert sur du parchemin, ruban vert en marque-page', 2, 'Des livres minces qu’on relit. Un poème le soir suffit.'),
        make('c-jeunesse', 'Jeunesse', 'jeunesse', 'rayon-jeunesse.webp', 'Étagère basse d’albums jeunesse et petit tabouret en osier', 2, 'Ce qu’on lit à voix haute, et ce que l’enfant relit tout seul six mois plus tard.'),
        make('c-beaux-livres', 'Beaux-livres', 'beaux-livres', 'rayon-beaux-livres.webp', 'Grands formats empilés à plat sur une table, l’un ouvert sur une photographie', 1, 'Grand format, papier épais. Les livres qui restent sur la table basse.'),
        make('c-bd', 'Bandes dessinées', 'bandes-dessinees', 'rayon-bd.webp', 'Présentoir en bois d’albums de bande dessinée vus de trois quarts', 0, 'Albums, romans graphiques et carnets dessinés.'),
    ];
}
//# sourceMappingURL=catalog.js.map